import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { DB_NODES } from "../../../../db/constants/nodeNames";
import { UserAlbumStickersMap } from "../../models/album/types";
import {
  getMissingStickerIds,
  getRepeatedStickerIds,
  Sticker,
  StickerCategory,
  stickerIdToSticker,
  UserWithStickers,
  UserWithStickersJSON,
} from "../../models/sticker/sticker";

export interface MatchesResult {
  matches: UserWithStickers[];
  potentialMatches: UserWithStickers[];
}

export interface StickerComparisonStickers {
  myStickers: Sticker[];
  thierStickers: Sticker[];
}

export interface StickerQuantityBreakDown {
  progress: number;
  missing: number;
  repeated: number;
}

function addStickersToCategories({
  pages,
  stickers,
}: {
  pages: StickerCategory[];
  stickers: Sticker[];
}): StickerCategory[] {
  stickers.forEach((sticker) => {
    // Find the pages this sticker belongs to.
    const page = pages.find(
      (page) => page.abbreviation === sticker.categoryAbbreviation,
    );

    // Determine if that sticker or it's placeholder is already in album.
    const alreadyAdded = page?.stickers?.find(
      (stickerOnPage) => stickerOnPage.number === sticker.number,
    );

    // If sticker or it's placeholder is already in album, overwrite quantity.
    if (alreadyAdded) {
      alreadyAdded.quantity = sticker.quantity;
    }

    // If sticker is not in album, add it.
    if (!alreadyAdded) {
      page?.stickers?.push(sticker);
    }

    // Ensure stickers are sorted by number.
    page?.stickers?.sort((a, b) => parseInt(a.number) - parseInt(b.number));
  });

  return pages;
}

function categoriesToStickerList(categories: StickerCategory[]): Sticker[] {
  return categories.map((category) => category.stickers || []).flat();
}

function compareStickers(
  stickers: StickerComparisonStickers,
): StickerComparisonStickers {
  const { myStickers, thierStickers } = stickers;

  const thierRepeatedIds = getRepeatedStickerIds(thierStickers);
  const myRepeatedIds = getRepeatedStickerIds(myStickers);
  const thierMissingIds = getMissingStickerIds(thierStickers);
  const myMissingIds = getMissingStickerIds(myStickers);

  const theirStickersThatINeed = thierRepeatedIds
    .filter((id) => myMissingIds.includes(id))
    .map(stickerIdToSticker);

  const myStickersThatTheyNeed = myRepeatedIds
    .filter((id) => thierMissingIds.includes(id))
    .map(stickerIdToSticker);

  return {
    thierStickers: theirStickersThatINeed,
    myStickers: myStickersThatTheyNeed,
  };
}

async function fetchUserStickers({
  albumId,
  userId,
}: {
  albumId?: string;
  userId?: string;
}): Promise<Sticker[]> {
  if (!userId) return [];

  const db = getFirestore();
  const document = doc(db, DB_NODES.USER_ALBUMS, userId);
  const userAlbumsDocSnap = await getDoc(document);

  let stickers: Sticker[] = [];

  if (userAlbumsDocSnap.exists()) {
    const userAlbumMap = userAlbumsDocSnap.data() as UserAlbumStickersMap;

    stickers = albumId
      ? userAlbumMap[albumId] || []
      : Object.values(userAlbumMap).reduce(
          (acc, value) => [...acc, ...value],
          [],
        );
  }

  return stickers;
}

async function fetchUserWithStickers({
  albumId,
  userId,
}: {
  albumId?: string;
  userId: string | undefined | null;
}): Promise<UserWithStickers | undefined> {
  if (!userId) return undefined;

  const db = getFirestore();
  const document = doc(db, DB_NODES.USERS, userId);
  const userDocSnap = await getDoc(document);

  const userJson = userDocSnap.data() as UserWithStickersJSON;
  const { most_granular_geo_area_id, name } = userJson;

  const stickers = await fetchUserStickers({
    albumId,
    userId,
  });

  return {
    mostGranularGeoAreaId: most_granular_geo_area_id,
    id: userId,
    missingStickerIds: getMissingStickerIds(stickers),
    name: name,
    repeatedStickerIds: getRepeatedStickerIds(stickers),
    stickers,
  };
}

function getQuantityBreakDown(stickers: Sticker[]): StickerQuantityBreakDown {
  const missing = stickers.filter((it) => it.quantity === 0).length;
  const repeated = stickers.filter((it) => it.quantity > 1).length;
  const progress = ((stickers.length - missing) / stickers.length) * 100;

  return { progress, missing, repeated };
}

async function updateUserStickers({
  albumId,
  stickers,
  userId,
}: {
  albumId: string;
  stickers: Sticker[];
  userId: string;
}): Promise<void> {
  // Create or update user's user album entry
  const db = getFirestore();
  const userAlbumDoc = doc(db, DB_NODES.USER_ALBUMS, userId);

  await setDoc(
    userAlbumDoc,
    {
      [albumId]: stickers,
    },
    { merge: true },
  );

  await updateUserMissingAndRepeated(userId);
}

async function findUserStickerMatches({
  geoAreaIds,
  myStickers,
}: {
  geoAreaIds: string[];
  myStickers: Sticker[];
}): Promise<MatchesResult> {
  // Get all users that have repeated stickers that I need
  const db = getFirestore();
  const usersCollection = collection(db, DB_NODES.USERS);

  const usersInMyTargetGeoAreas = await getDocs(
    query(
      usersCollection,
      where(DB_NODES.MOST_GRANULAR_GEO_AREA_ID, "in", geoAreaIds),
    ),
  );

  const usersInMyTargetGeoAreasWithStickersINeed: UserWithStickers[] = [];

  const myMissingIds = getMissingStickerIds(myStickers);

  usersInMyTargetGeoAreas.forEach((doc) => {
    const json = doc.data() as UserWithStickersJSON;
    const { name, stickers, most_granular_geo_area_id, missing, repeated } =
      json;
    if (repeated?.some((id) => myMissingIds.includes(id))) {
      usersInMyTargetGeoAreasWithStickersINeed.push({
        mostGranularGeoAreaId: most_granular_geo_area_id,
        id: doc.id,
        missingStickerIds: missing,
        name,
        repeatedStickerIds: repeated,
        stickers,
      });
    }
  });

  // Determine which of these users need any of my duplicates
  const myRepeatedIds = getRepeatedStickerIds(myStickers);

  const matches: UserWithStickers[] = [];
  const potentialMatches: UserWithStickers[] = [];

  usersInMyTargetGeoAreasWithStickersINeed.forEach((user) => {
    const intersectionsCount = myRepeatedIds.filter((id) =>
      user.missingStickerIds.includes(id),
    ).length;

    if (intersectionsCount) {
      user.quantityOfMatches = intersectionsCount;
      matches.push(user);
    } else {
      potentialMatches.push(user);
    }
  });

  return {
    matches,
    potentialMatches,
  };
}

async function updateUserMissingAndRepeated(userId: string): Promise<void> {
  const db = getFirestore();

  // Update user's missing and repeated fields if user document exists
  const albumsDocSnap = await getDoc(doc(db, DB_NODES.USER_ALBUMS, userId));

  if (!albumsDocSnap.exists()) return;

  const userDoc = doc(db, DB_NODES.USERS, userId);
  const userDocSnap = await getDoc(userDoc);
  if (!userDocSnap.exists) return;

  const userAlbumMap = albumsDocSnap.data() as UserAlbumStickersMap;

  const allStickers = Object.values(userAlbumMap).reduce(
    (acc, value) => [...acc, ...value],
    [],
  );

  await setDoc(
    userDoc,
    {
      [DB_NODES.MISSING]: getMissingStickerIds(allStickers),
      [DB_NODES.REPEATED]: getRepeatedStickerIds(allStickers),
    },
    { merge: true },
  );
}

const userStickerService = {
  addStickersToCategories,
  categoriesToStickerList,
  compareStickers,
  fetchUserStickers,
  fetchUserWithStickers,
  findUserStickerMatches,
  getQuantityBreakDown,
  updateUserMissingAndRepeated,
  updateUserStickers,
};

export default userStickerService;
